import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import type { PrerequisiteCourseBlock } from '@lms/shared-types';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { prerequisiteCourseLabel } from './PrerequisiteCourseGate';

interface EnrolledCourse {
  id: string;
  title: string;
  name?: string;
  code?: string;
  shortDescription?: string | null;
  progress?: number;
  progressPercent?: number;
  prerequisiteCourse?: PrerequisiteCourseBlock | null;
}

type CourseFilter = 'all' | 'in_progress' | 'completed';

const FILTERS: { value: CourseFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'completed', label: 'Completados' },
];

const COURSE_ICONS = ['terminal', 'science', 'menu_book', 'biotech', 'architecture'] as const;

function courseProgress(course: EnrolledCourse): number {
  return Math.round(course.progressPercent ?? course.progress ?? 0);
}

function courseTitle(course: EnrolledCourse): string {
  return course.title ?? course.name ?? 'Curso';
}

function courseIcon(courseId: string): string {
  let hash = 0;
  for (let i = 0; i < courseId.length; i += 1) {
    hash = (hash + courseId.charCodeAt(i)) % COURSE_ICONS.length;
  }
  return COURSE_ICONS[hash] ?? 'menu_book';
}

function courseDescription(course: EnrolledCourse): string {
  if (course.shortDescription?.trim()) return course.shortDescription.trim();
  return 'Explora el contenido del curso y da seguimiento a tu avance académico.';
}

function courseHref(course: EnrolledCourse): string {
  return `/mis-cursos/${course.id}`;
}

function matchesFilter(course: EnrolledCourse, filter: CourseFilter): boolean {
  const percent = courseProgress(course);
  if (filter === 'completed') return percent >= 100;
  if (filter === 'in_progress') return percent < 100;
  return true;
}

function matchesSearch(course: EnrolledCourse, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [courseTitle(course), course.code ?? ''].join(' ').toLowerCase();
  return haystack.includes(needle);
}

export function MisCursosPage() {
  const [filter, setFilter] = useState<CourseFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['student', 'courses'],
    queryFn: () => api<{ data: EnrolledCourse[] }>('/student/courses'),
    enabled: isAuthenticated,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });

  const courses = useMemo(() => {
    return (data?.data ?? []).filter(
      (course) => matchesFilter(course, filter) && matchesSearch(course, searchQuery),
    );
  }, [data?.data, filter, searchQuery]);

  return (
    <div className="lms-mycourses lms-site-admin-hub-page font-sans">
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-2 font-sans">
          <h1 className="dash-serif text-3xl font-bold text-dash-primary md:text-4xl">Mis cursos</h1>
          <p className="text-sm text-dash-on-surface-variant md:text-base">
            Explora tus materias matriculadas y da seguimiento a tu avance académico.
          </p>
        </header>

        <div className="mc-toolbar">
          <div className="mc-filters" role="group" aria-label="Filtrar cursos">
            {FILTERS.map((chip) => (
              <button
                key={chip.value}
                type="button"
                className={`mc-filter${filter === chip.value ? ' mc-filter--active' : ''}`}
                onClick={() => setFilter(chip.value)}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="mc-search">
            <span className="mc-search__icon" aria-hidden="true">
              <span className="material-symbols-outlined text-[20px]">search</span>
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mc-search__input"
              placeholder="Buscar curso por nombre o código..."
              aria-label="Buscar curso por nombre o código"
            />
          </div>
        </div>

        {error && (
          <p className="mc-error" role="alert">
            {(error as Error).message}
          </p>
        )}

        {isLoading || authLoading ? (
          <div className="mc-loading">
            <Loader />
          </div>
        ) : courses.length === 0 ? (
          <div className="mc-empty">
            <div className="mc-empty__icon" aria-hidden="true">
              <span className="material-symbols-outlined text-[32px]">folder_open</span>
            </div>
            <h2 className="mc-empty__title">No se encontraron cursos</h2>
            <p className="mc-empty__text">
              No existen materias que coincidan con los filtros aplicados o tu término de búsqueda.
            </p>
          </div>
        ) : (
          <div className="mc-grid">
            {courses.map((course, index) => {
              const title = courseTitle(course);
              const percent = courseProgress(course);
              const block = course.prerequisiteCourse ?? null;
              const locked = Boolean(block && block.status !== 'aprobado');
              const completed = !locked && percent >= 100;
              const showContinue = !locked && percent > 0;
              const actionHref =
                locked && block && (block.status === 'sin_intento' || block.status === 'rechazado')
                  ? `/mis-cursos/${block.prerequisiteCourseId}`
                  : courseHref(course);
              const actionLabel = locked
                ? block?.status === 'en_revision'
                  ? 'Ver estado'
                  : 'Ir al curso previo'
                : showContinue
                  ? 'Continuar'
                  : 'Entrar al curso';

              return (
                <article key={course.id} className="mc-card">
                  <div
                    className={`mc-card__accent ${index % 2 === 0 ? 'mc-card__accent--a' : 'mc-card__accent--b'}`}
                    aria-hidden="true"
                  />
                  <div>
                    <span
                      className={`mc-card__badge${completed ? ' mc-card__badge--completed' : ''}`}
                    >
                      <span
                        className={`mc-card__badge-dot${completed ? '' : ' mc-card__badge-dot--pulse'}`}
                        aria-hidden="true"
                      />
                      {locked ? 'Bloqueado' : completed ? 'Completado' : 'En curso'}
                    </span>
                    <div className="mc-card__head">
                      <h2 className="mc-card__title">{title}</h2>
                      <div className="mc-card__icon" aria-hidden="true">
                        <span className="material-symbols-outlined text-[24px]">
                          {courseIcon(course.id)}
                        </span>
                      </div>
                    </div>
                    <p className="mc-card__description">{courseDescription(course)}</p>
                    {locked && block ? (
                      <div className="mc-card__progress">
                        <p className="text-sm text-dash-on-surface-variant">
                          {block.status === 'sin_intento'
                            ? `Debes rendir la evaluación de ${prerequisiteCourseLabel(block)}`
                            : block.status === 'rechazado'
                              ? 'Tu evaluación fue rechazada'
                              : 'Tu evaluación está en revisión'}
                        </p>
                        {block.status === 'rechazado' && block.rejectionReason ? (
                          <p className="mt-2 text-sm text-red-700">{block.rejectionReason}</p>
                        ) : null}
                      </div>
                    ) : (
                    <div className="mc-card__progress">
                      <div className="mc-card__progress-labels">
                        <span>Progreso general</span>
                        <span>{percent}% completado</span>
                      </div>
                      <div className="mc-card__progress-track">
                        <div
                          className="mc-card__progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    )}
                  </div>
                  <div className="mc-card__footer">
                    <span className="mc-card__code">Código: {course.code ?? 'SIN-COD'}</span>
                    <Link
                      to={actionHref}
                      className={`mc-card__btn ${showContinue ? 'mc-card__btn--continue' : 'mc-card__btn--enter'}`}
                    >
                      <span>{actionLabel}</span>
                      <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                        {showContinue ? 'play_circle' : 'arrow_forward'}
                      </span>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
